import { useState, useEffect } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Gift, Copy, Check, Users, Trophy } from "lucide-react";
import { toast } from "sonner";
import ShareTiles from "@/components/ShareTiles";
import { useLocale } from "@/i18n/LocaleContext";

export default function ReferralCard() {
  const { t } = useLocale();
  const [data, setData] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [copied, setCopied] = useState(false);

  const statusLabel = {
    joined: { text: t("ref.stJoined"), cls: "text-muted-foreground" },
    started_chatting: { text: t("ref.stChatting"), cls: "text-green-400" },
    completed_date: { text: t("ref.stDate"), cls: "text-green-400" },
    purchased_slot: { text: t("ref.stSlot"), cls: "text-green-400" },
  };

  useEffect(() => {
    api.get("/referrals").then((res) => setData(res.data)).catch(() => {});
    api.get("/referrals/leaderboard").then((res) => setLeaderboard(res.data.leaderboard)).catch(() => {});
  }, []);

  if (!data) return null;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(data.referral_link);
    } catch {
      const el = document.createElement("textarea");
      el.value = data.referral_link;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopied(true);
    toast.success(t("ref.copied"));
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="glass-card p-6 mb-6" data-testid="referral-card">
      <div className="flex items-center gap-3 mb-3">
        <Gift className="w-5 h-5 text-primary" />
        <h3 className="text-lg font-semibold">{t("ref.title")}</h3>
      </div>

      <p className="text-sm text-muted-foreground mb-4">
        {t("ref.desc")}
      </p>

      <div className="flex items-center gap-2 mb-4">
        <div className="flex-1 px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm font-mono truncate select-all" data-testid="referral-link">
          {data.referral_link}
        </div>
        <Button size="sm" variant="outline" onClick={copyLink} className="border-white/10 shrink-0" data-testid="copy-referral-btn">
          {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
        </Button>
      </div>

      <ShareTiles link={data.referral_link} />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <div className="p-3 rounded-lg bg-white/5 text-center">
          <p className="text-xl font-bold text-primary" data-testid="referred-count">{data.referred_count}</p>
          <p className="text-xs text-muted-foreground">{t("ref.friendsReferred")}</p>
        </div>
        <div className="p-3 rounded-lg bg-white/5 text-center">
          <p className="text-xl font-bold text-amber-400" data-testid="referral-bonus-credits">{data.chat_bonus_credits || 0}</p>
          <p className="text-xs text-muted-foreground">{t("ref.bonusCredits")}</p>
        </div>
        <div className="p-3 rounded-lg bg-white/5 text-center">
          <p className="text-xl font-bold text-green-400" data-testid="referral-free-slots">{data.free_slots_earned}</p>
          <p className="text-xs text-muted-foreground">{t("ref.freeSlots")}</p>
        </div>
        <div className="p-3 rounded-lg bg-white/5 text-center">
          <p className="text-xl font-bold text-green-400" data-testid="referral-rewards">{data.payout_boosts}</p>
          <p className="text-xs text-muted-foreground">{t("ref.boosts")}</p>
        </div>
      </div>

      {data.referred.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" /> {t("ref.yourReferrals")}
          </p>
          {data.referred.slice(0, 8).map((r, i) => (
            <div key={i} className="flex items-center justify-between text-sm px-3 py-2 rounded-lg bg-white/5">
              <span>{r.name}</span>
              <span className={`text-xs ${statusLabel[r.status]?.cls || ""}`}>
                {statusLabel[r.status]?.text || r.status}
              </span>
            </div>
          ))}
        </div>
      )}

      {leaderboard.length > 0 && (
        <div className="mt-4 pt-4 border-t border-white/10" data-testid="referral-leaderboard">
          <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5 mb-2">
            <Trophy className="w-3.5 h-3.5 text-yellow-400" /> {t("ref.topReferrers")}
          </p>
          <div className="space-y-1.5">
            {leaderboard.map((entry, i) => (
              <div key={i} className="flex items-center justify-between text-sm px-3 py-2 rounded-lg bg-white/5">
                <span className="flex items-center gap-2">
                  <span className={`w-5 text-center font-bold ${i === 0 ? "text-yellow-400" : i === 1 ? "text-gray-300" : i === 2 ? "text-amber-600" : "text-muted-foreground"}`}>
                    {i + 1}
                  </span>
                  {entry.name}
                </span>
                <span className="text-xs text-muted-foreground">{entry.referrals} {t("ref.referralsWord")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
